import React from 'react';
import { TablePaginationPlaceholder } from 'shared/placeholders';
import { TablePagination } from '../components/TablePagination/TablePagination';
import { Pageable } from '../models/Pageable';
import { useDataFetcher } from 'shared/http';
import { Alert } from '@orbita-ui/core';
import { PagedResponseWithAggregation } from '../models/PagedResponseWithAggregation';

export interface TableComponentParams<T> {
  tableContent: T[];
  placeholderRows?: number;
  loadingState?: boolean;
  totales?: T;
}

export type FetcherResponse<T> = Pageable<T> | PagedResponseWithAggregation<T>;

interface PaginatedTableComponentParams<T> {
  dataFetcherFunction: (status: {
    pageNumber: number;
    pageSize: number;
  }) => Promise<FetcherResponse<T>>;
  pageableInit?: FetcherResponse<T>;
  noResultsMessages?: string;
  loadingState?: boolean;
}

type TableComponent<T> = React.ComponentType<TableComponentParams<T>>;

export function withPagination<T, P extends TableComponentParams<T>>(
  TableComponent: TableComponent<T>,
) {
  return ({
    pageableInit,
    dataFetcherFunction,
    loadingState,
    noResultsMessages,
    ...props
  }: Omit<P, 'tableContent' | 'totales'> & PaginatedTableComponentParams<T>) => {
    const [response, setRequest] = useDataFetcher<FetcherResponse<T>>(pageableInit);

    const onChangePaginationStatus = (status: {
      pageNumber: number;
      pageSize: number;
    }) => {
      setRequest(() => dataFetcherFunction(status));
    };

    const responseData = response.data;
    let pageableData: Pageable<T> | undefined;
    let totalesData: T | undefined;

    if (responseData) {
      // Verificamos si la respuesta tiene la propiedad 'totales'
      if ('totales' in responseData) {
        pageableData = responseData.page;
        totalesData = responseData.totales;
      } else {
        pageableData = responseData;
      }
    }

    const isLoading = loadingState || response.isLoading;
    const hasContent = pageableData?.content?.length;

    return (
      <>
        {isLoading || hasContent ? (
          <>
            <TableComponent
              tableContent={isLoading ? null : pageableData?.content}
              totales={totalesData}
              placeholderRows={pageableData?.content?.length}
              loadingState={isLoading}
              {...props}
            ></TableComponent>
            {loadingState || !pageableData?.content ? (
              <TablePaginationPlaceholder />
            ) : (
              <TablePagination
                pageable={pageableData}
                onChangePaginationStatus={onChangePaginationStatus}
              ></TablePagination>
            )}
          </>
        ) : (
          <div className="p-3">
            <Alert
              variant="info"
              fullWidth
              subtitle={noResultsMessages || 'No se encontraron resultados'}
            />
          </div>
        )}
      </>
    );
  };
}
